import React, { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Image as ImageIcon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface DefaultMenuItem {
  id: string
  name: string
  description: string
  price: number
  category: 'main' | 'addon'
  image_url: string
  display_order: number
  lobster_tail_qty: number
}

interface DefaultMenuCatalogProps {
  onAddItem: (item: DefaultMenuItem) => void
  isCollapsed: boolean
  onToggleCollapse: () => void
}

export const DefaultMenuCatalog: React.FC<DefaultMenuCatalogProps> = ({
  onAddItem,
  isCollapsed,
  onToggleCollapse
}) => {
  const [items, setItems] = useState<DefaultMenuItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDefaultItems()
  }, [])

  const fetchDefaultItems = async () => {
    try {
      const { data, error } = await supabase
        .from('default_menu_items')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('display_order', { ascending: true })

      if (error) throw error
      console.log('[DefaultMenuCatalog] Fetched items:', data)
      setItems(data || [])
    } catch (error) {
      console.error('Error fetching default menu items:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Loading Failed"
          message="Could not load catalog items"
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  if (isCollapsed) {
    return (
      <div className="mb-6 p-4 bg-muted rounded-lg border border-border">
        <button
          onClick={onToggleCollapse}
          className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors w-full"
        >
          <span className="text-lg">🚀</span>
          <span>Quick Add from Catalog</span>
          <ChevronDown size={16} className="ml-auto" />
        </button>
      </div>
    )
  }

  const mainItems = items.filter(item => item.category === 'main')
  const addonItems = items.filter(item => item.category === 'addon')

  return (
    <div className="mb-8 p-6 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 rounded-lg border border-red-200 dark:border-red-800">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🚀</span>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Quick Add from Catalog
          </h3>
        </div>
        <button
          onClick={onToggleCollapse}
          type="button"
          className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <span>Collapse</span>
          <ChevronUp size={16} />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground mt-2">Loading catalog...</p>
        </div>
      ) : (
        <>
          {/* Main Items */}
          {mainItems.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                Main Items
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {mainItems.map(item => (
                  <CatalogCard key={item.id} item={item} onAdd={onAddItem} />
                ))}
              </div>
            </div>
          )}

          {/* Add-Ons */}
          {addonItems.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                Add-Ons
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {addonItems.map(item => (
                  <CatalogCard key={item.id} item={item} onAdd={onAddItem} />
                ))}
              </div>
            </div>
          )}

          {items.length === 0 && !loading && (
            <p className="text-center text-gray-600 dark:text-gray-400 py-4">
              No catalog items available
            </p>
          )}
        </>
      )}
    </div>
  )
}

const CatalogCard: React.FC<{
  item: DefaultMenuItem
  onAdd: (item: DefaultMenuItem) => void
}> = ({ item, onAdd }) => {
  const [imageError, setImageError] = useState(false)

  return (
    <button
      onClick={() => onAdd(item)}
      type="button"
      className="catalog-card group relative rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-[#962831] hover:shadow-lg transition-all duration-200 p-4 text-left w-full"
      style={{
        backgroundColor: 'white',
        color: 'inherit'
      }}
    >
      {/* Product Image */}
      <div className="aspect-square mb-3 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        {item.image_url && !imageError ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => {
              console.error('Failed to load image:', item.image_url)
              setImageError(true)
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400">
            <span className="text-4xl">📦</span>
            <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>No image</p>
          </div>
        )}
      </div>

      {/* Item Name */}
      <h5
        className="font-medium text-sm mb-1 line-clamp-2 min-h-[2.5rem]"
        style={{ color: '#111827' }}
      >
        {item.name}
      </h5>

      {/* Price */}
      <p
        className="text-lg font-bold"
        style={{ color: '#862219' }}
      >
        ${item.price.toFixed(2)}
      </p>

      {/* Hover Overlay */}
      <div
        className="absolute inset-0 rounded-lg transition-all duration-200 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100"
        style={{
          backgroundColor: 'rgba(134, 34, 25, 0.1)'
        }}
      >
        <span
          className="font-semibold text-sm px-3 py-1 rounded-md shadow-lg pointer-events-auto"
          style={{
            color: '#862219',
            backgroundColor: 'white'
          }}
        >
          + Add to Menu
        </span>
      </div>
    </button>
  )
}

export default DefaultMenuCatalog
